import React, { useState, useEffect } from 'react';
import { Select, Input, Button, message, Upload, Radio, Spin, Tooltip, Table, Typography } from 'antd';
import { FileText, Upload as UploadIcon } from 'lucide-react';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';


const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

// Field mapping for Plan Design and Elections
const fieldMapping = {
  "Plan Type": "plan_type",
  "Eligibility Requirements": "eligibility_requirements",
  "Automatic Enrollment": "automatic_enrollment",
  "Entry Date": "entry_date",
  "Eligible Contribution Types": "eligible_contribution_types",
  "Contribution Compensation Definition": "contribution_compensation_definition",
  "Roth Contributions Allowed": "roth_contributions_allowed",
  "Roth Contribution Rules or Conditions": "roth_contribution_rules_or_conditions",
  "Catch-Up Contributions Allowed": "catch_up_contributions_allowed",
  "Employer Match Formula": "employer_match_formula",
  "True-Up Provision": "true_up_provision",
  "Loans (Availability and Details)": "loans_availability_and_details",
  "Hardship Distributions": "hardship_distributions",
  "In Service Distributions": "in_service_distributions",
  "Employer Matching Vesting Schedule": "employer_matching_vesting_schedule",
  "Non-Elective Vesting Schedule": "non_elective_vesting_schedule",
  "QDIA": "qdia"
};

// Display names for a professional look
const displayNameMap = {
  company_name: "Company Name",
  plan_type: "Plan Type",
  eligibility_requirements: "Eligibility Requirements",
  automatic_enrollment: "Automatic Enrollment",
  entry_date: "Entry Date",
  eligible_contribution_types: "Eligible Contribution Types",
  contribution_compensation_definition: "Contribution Compensation Definition",
  roth_contributions_allowed: "Roth Contributions Allowed",
  roth_contribution_rules_or_conditions: "Roth Contribution Rules or Conditions",
  catch_up_contributions_allowed: "Catch-Up Contributions Allowed",
  employer_match_formula: "Employer Match Formula",
  true_up_provision: "True-Up Provision",
  loans_availability_and_details: "Loans (Availability and Details)",
  hardship_distributions: "Hardship Distributions",
  in_service_distributions: "In Service Distributions",
  employer_matching_vesting_schedule: "Employer Matching Vesting Schedule",
  non_elective_vesting_schedule: "Non-Elective Vesting Schedule",
  qdia: "QDIA"
};



const AutomaticDataEntryForPlanDesignAndElections = ({ onAddRow, onDataSubmitted }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    plan_type: '',
    eligibility_requirements: '',
    automatic_enrollment: 'No',
    entry_date: '',
    eligible_contribution_types: '',
    contribution_compensation_definition: '',
    roth_contributions_allowed: '',
    roth_contribution_rules_or_conditions: '',
    catch_up_contributions_allowed: '',
    employer_match_formula: '',
    true_up_provision: '',
    loans_availability_and_details: '',
    hardship_distributions: '',
    in_service_distributions: '',
    employer_matching_vesting_schedule: '',
    non_elective_vesting_schedule: '',
    qdia: ''
  });

  const [inputMethod, setInputMethod] = useState('manual');
  const [fileList, setFileList] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyData, setCompanyData] = useState([]);
  const [extractedData, setExtractedData] = useState(null);
  const [showReview, setShowReview] = useState(false);

  // Fetch client names
  useEffect(() => {
    const fetchClientNames = async () => {
      try {
        const userId = localStorage.getItem('userid');
       const year = dayjs().year();; 
        const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/client_names/${year}`, {
          headers: { 'user-id': userId }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        const options = data.client_names.map(name => ({
          value: name,
          label: name
        }));
        setCompanyOptions(options);
      } catch (error) {
        console.error('Error fetching client names:', error);
        message.error('Failed to load client list');
      }
    };

    fetchClientNames();
  }, []);

  // Fetch existing Plan Design & Elections data
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const userId = localStorage.getItem('userid');
       const year = dayjs().year();;
        const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/spreadsheet/${userId}/Plan Design and Elections/${year}`, {
          headers: { 'user-id': userId }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Fetched Plan Design and Elections data:", data);
        setCompanyData(data.data || []);
      } catch (error) {
        console.error('Error fetching Plan Design data:', error);
        message.error('Failed to load plan design data');
      }
    };

    fetchCompanyData();
  }, []);

  // Prefill formData when company_name or companyData changes
  useEffect(() => {
    if (formData.company_name && companyData.length > 0) {
      const selectedCompany = companyData.find(c => c["Company Name"] === formData.company_name);
      if (selectedCompany) {
        const updatedData = { ...formData };
        for (const [spreadsheetKey, formKey] of Object.entries(fieldMapping)) {
          if (selectedCompany.hasOwnProperty(spreadsheetKey) && selectedCompany[spreadsheetKey] !== null) {
            updatedData[formKey] = selectedCompany[spreadsheetKey];
          }
        }

        if (!updatedData.automatic_enrollment) {
          updatedData.automatic_enrollment = 'No';
        }

        setFormData(updatedData);
      }
    }
  }, [formData.company_name, companyData]);

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    try {
      const userId = localStorage.getItem('userid');
      const year = dayjs().year();

      const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/plan-design/${year}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': userId,
          'accept': 'application/json'
        },
        body: JSON.stringify(formData)  // Send formData directly as it already has the correct structure
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (inputMethod === 'manual') {
        message.success(data.message || 'Plan design data submitted successfully');
      } else {
        message.success('Data saved successfully with your edits');
      }

      // Clear form after submission
      setFormData({
        company_name: '',
        plan_type: '',
        eligibility_requirements: '',
        automatic_enrollment: 'No',
        entry_date: '',
        eligible_contribution_types: '',
        contribution_compensation_definition: '',
        roth_contributions_allowed: '',
        roth_contribution_rules_or_conditions: '',
        catch_up_contributions_allowed: '',
        employer_match_formula: '',
        true_up_provision: '',
        loans_availability_and_details: '',
        hardship_distributions: '',
        in_service_distributions: '',
        employer_matching_vesting_schedule: '',
        non_elective_vesting_schedule: '',
        qdia: ''
      });

      if (onDataSubmitted) {
        onDataSubmitted();
      }

    } catch (error) {
      console.error('Error submitting data:', error);
      message.error('Failed to save data: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const isFormValid = () => {
    if (inputMethod === 'manual') {
      return formData.company_name !== '';
    } else {
      return fileList.length > 0 && formData.company_name !== '';
    }
  };

  const handleFileUpload = (info) => {
    let newFileList = [...info.fileList].slice(-1); // Keep only the latest file
    setFileList(newFileList);

    const status = info.file.status;
    if (status === 'done') {
      message.success(`${info.file.name} file uploaded successfully.`);
    } else if (status === 'error') {
      message.error(`${info.file.name} file upload failed.`);
    }
  };

  const getTooltipText = (key) => {
    const tooltips = {
      plan_type: "Select the type of retirement plan (e.g., 401(k), 403(b), etc.)",
      eligibility_requirements: "Specify the conditions employees must meet to participate in the plan",
      automatic_enrollment: "Indicate if employees are automatically enrolled in the plan",
      entry_date: "When eligible employees can begin participating in the plan",
      eligible_contribution_types: "Types of contributions allowed (e.g., pre-tax, Roth, after-tax)",
      contribution_compensation_definition: "Definition of compensation used for contributions",
      roth_contributions_allowed: "Whether Roth contributions are permitted",
      roth_contribution_rules_or_conditions: "Special rules or conditions for Roth contributions",
      catch_up_contributions_allowed: "Whether catch-up contributions are permitted",
      employer_match_formula: "Describe the employer matching contribution formula",
      true_up_provision: "Details about true-up contributions if applicable",
      loans_availability_and_details: "Loan provisions and requirements",
      hardship_distributions: "Rules and conditions for hardship withdrawals",
      in_service_distributions: "Requirements for in-service distributions",
      employer_matching_vesting_schedule: "Vesting schedule for employer matching contributions",
      non_elective_vesting_schedule: "Vesting schedule for non-elective employer contributions",
      qdia: "Qualified Default Investment Alternative details"
    };
    return tooltips[key] || "Enter details";
  };

  const handlePDFProcess = async () => {
    try {
      if (fileList.length === 0 || !formData.company_name) {
        message.error('Please select both a company and a PDF file');
        return;
      }

      setIsProcessing(true);
      const formDataObj = new FormData();
      formDataObj.append('file', fileList[0].originFileObj);
      formDataObj.append('company_name', formData.company_name);

      const userId = localStorage.getItem('userid');
      const year = dayjs().year();

      const response = await fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/plan-design/${year}/process-pdf`, {
        method: 'POST',
        headers: { 'user-id': userId },
        body: formDataObj
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.extracted_data) {
        // Store the extracted data directly without transformation
        setExtractedData(data.extracted_data);
        setShowReview(true);
      } else {
        throw new Error('No data extracted from PDF');
      }

    } catch (error) {
      console.error('Error processing PDF:', error);
      message.error('Failed to process PDF: ' + error.message);
      // Reset state
      setFileList([]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveExtractedData = () => {
    // Helper function to get structured data from fields
    const getFormattedField = (field) => {
      if (typeof field === 'string' && field) {
        // If it's already a formatted string, return as is
        return field;
      } else if (typeof field === 'object' && field !== null) {
        // If it's an object with metadata, format it appropriately
        const value = field.value || '';
        const humanSummary = field.human_summary || 'No summary available';
        const page = field.page || 'N/A';
        const section = field.section || 'N/A';
        
        return `Summary: ${humanSummary}\n\nPage(s): ${page}\n\nSection(s): ${section}\n\nVerbatim Extracted Text: ${value}`;
      }
      return '';
    };

    // Create a new form data object with the edited extracted data
    const newFormData = {
      company_name: formData.company_name,
      plan_type: getFormattedField(extractedData.plan_type),
      eligibility_requirements: getFormattedField(extractedData.eligibility_requirements),
      automatic_enrollment: getFormattedField(extractedData.automatic_enrollment),
      entry_date: getFormattedField(extractedData.entry_date),
      eligible_contribution_types: getFormattedField(extractedData.eligible_contribution_types),
      contribution_compensation_definition: getFormattedField(extractedData.contribution_compensation_definition),
      roth_contributions_allowed: getFormattedField(extractedData.roth_contributions_allowed),
      roth_contribution_rules_or_conditions: getFormattedField(extractedData.roth_contribution_rules_or_conditions),
      catch_up_contributions_allowed: getFormattedField(extractedData.catch_up_contributions_allowed),
      employer_match_formula: getFormattedField(extractedData.employer_match_formula),
      true_up_provision: getFormattedField(extractedData.true_up_provision),
      loans_availability_and_details: getFormattedField(extractedData.loans_availability_and_details),
      hardship_distributions: getFormattedField(extractedData.hardship_distributions),
      in_service_distributions: getFormattedField(extractedData.in_service_distributions),
      employer_matching_vesting_schedule: getFormattedField(extractedData.employer_matching_vesting_schedule),
      non_elective_vesting_schedule: getFormattedField(extractedData.non_elective_vesting_schedule),
      qdia: getFormattedField(extractedData.qdia)
    };

    // Log the data being sent
    console.log('Sending form data:', newFormData);

    // Submit the form with the new data
    const userId = localStorage.getItem('userid');
    const year = dayjs().year();

    fetch(`https://plansync-backend-234288438538.us-central1.run.app/api/plan-design/${year}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'user-id': userId,
        'accept': 'application/json'
      },
      body: JSON.stringify(newFormData)
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(() => {
      setShowReview(false);
      setExtractedData(null);
      setFileList([]);
      message.success('Data saved successfully with your edits');
      if (onDataSubmitted) {
        onDataSubmitted();
      }
    })
    .catch(error => {
      console.error('Error saving data:', error);
      message.error('Failed to save data: ' + error.message);
    });
  };

  const renderExtractedDataReview = () => {
    if (!extractedData) return null;

    console.log('Current extracted data:', extractedData); // Add logging to debug

    const dataSource = Object.entries(extractedData)
      .filter(([key]) => key !== 'company_name' && key !== 'message')
      .map(([key, value]) => ({
        key: key,
        fieldKey: key, // Original key for data operations
        field: displayNameMap[key] || key, // Use display name for UI
        value: value || '', // This could be an object or a string
        editable: true,
      }));

    console.log('Table data source:', dataSource); // Add logging to debug

    const columns = [
      {
        title: 'Field',
        dataIndex: 'field',
        key: 'field',
        width: '30%',
        render: (text) => {
          return <span className="font-medium text-gray-700">{text}</span>;
        }
      },
      {
        title: 'Extracted Value',
        dataIndex: 'value',
        key: 'value',
        width: '70%',
        render: (text, record) => {
          // Handle objects that have nested properties
          const processValue = (val) => {
            if (typeof val === 'object' && val !== null) {
              // If it's an object with expected structure
              return {
                value: val.value || 'No Data Found - Enter Manually',
                human_summary: val.human_summary || 'No summary available',
                page: val.page || 'N/A',
                section: val.section || 'N/A'
              };
            }
            return {
              value: val || '',
              human_summary: 'No summary available',
              page: 'N/A',
              section: 'N/A'
            };
          };

          // Process the value
          const processedValue = processValue(record.value);
          
          // For automatic enrollment, use a Select component
          if (record.fieldKey === 'automatic_enrollment') {
            return (
              <div>
                <Select
                  value={typeof record.value === 'string' ? record.value : processedValue.value}
                  style={{ width: '100%', marginBottom: '8px' }}
                  onChange={(value) => {
                    // Simple update - just store the selected value
                    setExtractedData(prev => ({
                      ...prev,
                      [record.fieldKey]: value
                    }));
                  }}
                >
                  <Option value="Yes">Yes</Option>
                  <Option value="No">No</Option>
                  <Option value="No Data Found - Enter Manually">No Data Found - Enter Manually</Option>
                </Select>
                {/* Display metadata */}
                <div className="text-xs text-gray-500 mt-1">
                  <div><strong>Summary:</strong> {processedValue.human_summary}</div>
                  <div><strong>Page(s):</strong> {processedValue.page}</div>
                  <div><strong>Section(s):</strong> {processedValue.section}</div>
                </div>
              </div>
            );
          }
          
          // For other fields, use TextArea
          return (
            <div>
              <TextArea
                value={typeof record.value === 'string' 
                  ? record.value 
                  : `Summary: ${processedValue.human_summary}\n\nPage(s): ${processedValue.page}\n\nSection(s): ${processedValue.section}\n\nVerbatim Extracted Text: ${processedValue.value}`
                }
                autoSize={{ minRows: 6, maxRows: 12 }}
                onChange={(e) => {
                  const fullText = e.target.value;
                  
                  // Simple update - just store the full text as the new value
                  setExtractedData(prev => ({
                    ...prev,
                    [record.fieldKey]: fullText
                  }));
                }}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 mb-2"
              />
              {/* Display metadata below for reference */}
              <div className="text-xs text-gray-500 mt-1">
                <div><strong>Summary:</strong> {processedValue.human_summary}</div>
                <br></br>
                <div><strong>Page(s):</strong> {processedValue.page}</div>
                <br></br>
                <div><strong>Section(s):</strong> {processedValue.section}</div>
                <br></br>
                <div><strong>Verbatim Extracted Text:</strong> {processedValue.value}</div>
              </div>
            </div>
          );
        },
      }
    ];

    return (
      <div className="mt-8">
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4">
          <p className="text-amber-800">
            <strong>Review and Edit:</strong> The information below was extracted from your PDF. 
            Please review and edit any fields as needed before saving.
          </p>
          <p className="text-amber-700 mt-2">
            Company Name: <strong>{formData.company_name}</strong>
          </p>
        </div>
        
        <Title level={4}>Review Extracted Data</Title>
        <Table 
          dataSource={dataSource} 
          columns={columns} 
          pagination={false}
          className="mb-4"
          rowClassName="align-top"
        />
        
        <div className="flex justify-end space-x-4 mt-4">
          <Button onClick={() => {
            setShowReview(false);
            setExtractedData(null);
            setFileList([]);
          }}>
            Cancel
          </Button>
          <Button 
            type="primary"
            onClick={handleSaveExtractedData}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Save Edited Data
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-8 bg-white rounded-xl">
      <h1 className="text-3xl font-light text-gray-800 mb-6">Plan Design and Elections Data Entry</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-1 gap-8 mb-8">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center">
              <span>Company Name</span>
              <Tooltip title="Select the company whose plan design you're documenting.">
                <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
              </Tooltip>
            </div>
          </label>
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder="Select a company"
            value={formData.company_name}
            onChange={(value) => handleChange('company_name', value)}
            className="text-lg"
            filterOption={(input, option) => 
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {companyOptions.sort((a, b) => a.label.localeCompare(b.label)).map(option => (
              <Option key={option.value} value={option.value}>{option.label}</Option>
            ))}
          </Select>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center">
              <span>Input Method</span>
              <Tooltip title="Choose between manual data entry or uploading a PDF of the plan document.">
                <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
              </Tooltip>
            </div>
          </label>
          <Radio.Group 
            onChange={(e) => setInputMethod(e.target.value)} 
            value={inputMethod}
            className="flex space-x-4"
          >
            <Radio value="manual" className="text-lg">Manual Entry</Radio>
            <Radio value="pdf" className="text-lg">Upload PDF</Radio>
          </Radio.Group>
        </div>
      </div>

      {inputMethod === 'manual' && !showReview && (
        <div className="grid grid-cols-1 gap-6">
          {Object.entries(formData)
            .filter(([key]) => key !== 'company_name')
            .map(([key, value]) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center">
                    <span>{displayNameMap[key] || key}</span>
                    <Tooltip title={getTooltipText(key)}>
                      <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
                    </Tooltip>
                  </div>
                </label>
                {key === 'automatic_enrollment' ? (
                  <Select
                    value={value}
                    onChange={(val) => handleChange(key, val)}
                    style={{ width: '100%' }}
                    placeholder={`Enter ${displayNameMap[key] || key}...`}
                  >
                    <Option value="Yes">Yes</Option>
                    <Option value="No">No</Option>
                    <Option value="No Data Found - Enter Manually">No Data Found - Enter Manually</Option>
                  </Select>
                ) : (
                  <TextArea
                    value={value}
                    onChange={(e) => handleChange(key, e.target.value)}
                    rows={3}
                    placeholder={`Enter ${displayNameMap[key] || key}...`}
                    className="w-full"
                  />
                )}
              </div>
            ))}
        </div>
      )}

      {inputMethod === 'pdf' && !showReview && (
        <div className="bg-gray-50 p-6 rounded-lg border-2 border-dashed border-gray-300">
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-blue-800">
              <strong>Note:</strong> For best results, please upload a "Plan Adoption Agreement" or "Plan Highlights" PDF. Processing may take up to a minute.
            </p>
          </div>
          <Tooltip title="Upload a PDF of the plan document. The system will automatically extract key plan design information.">
            <Upload.Dragger
              name="file"
              multiple={false}
              fileList={fileList}
              onChange={handleFileUpload}
              accept=".pdf"
              beforeUpload={() => false}
              className="bg-white"
            >
              <p className="ant-upload-drag-icon">
                <UploadIcon className="h-16 w-16 text-blue-500 mx-auto" />
              </p>
              <p className="ant-upload-text text-lg font-medium mt-4">
                Click or drag file to this area to upload
              </p>
              <p className="ant-upload-hint text-sm text-gray-500">
                Support for a single PDF file. The file will be processed to extract plan design information.
              </p>
            </Upload.Dragger>
          </Tooltip>
        </div>
      )}

      {showReview && renderExtractedDataReview()}

      {!showReview && (
        <div className="mt-8 flex justify-end">
          <Button 
            onClick={inputMethod === 'manual' ? handleSubmit : handlePDFProcess}
            disabled={!isFormValid() || isProcessing}
            size="large"
            className={`inline-flex items-center px-6 py-3 border border-transparent text-lg font-medium rounded-md shadow-sm text-white ${
              isFormValid() && !isProcessing 
                ? 'bg-blue-600 hover:bg-blue-700' 
                : 'bg-gray-300 cursor-not-allowed'
            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out`}
          >
            {isProcessing ? (
              <Spin className="mr-2" />
            ) : (
              <FileText className="h-5 w-5 mr-2" />
            )}
            {inputMethod === 'manual' ? 'Submit Plan Design' : (isProcessing ? 'Processing PDF...' : 'Process PDF')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AutomaticDataEntryForPlanDesignAndElections;